<template>
  <div
    class="chem-container-attach-wrapper chemtracker-module"
    data-test="outside-vif"
  >
    <fieldset>
      <legend>Container Attachments</legend>

      <div v-if="showFileListing">
        <file-attachment-listing
          :files="entity.field_chemical_container_files"
          allow-file-download
          :allow-file-remove="editMode"
          @file-deleted="deleteAttachedFile"
        />
      </div>

      <b-img
        v-if="editMode"
        id="upload-photos-icon"
        class="upload-photos-icon"
        :src="getUploadPhotosIconPath"
        alt="Upload Attachments"
      />
      <div v-if="editMode" class="upload-photos-wrapper">
        <b-row>
          <b-col>
            <b-form-file
              v-model="newFiles"
              label="Attach new file"
              id="chem_container_files"
              multiple
              accept="image/jpeg, image/png, image/gif, application/pdf, application/msword,
              application/vnd.openxmlformats-officedocument.wordprocessingml.document,
              application/vnd.openxmlformats-officedocument.presentationml.presentation, text/plain, text/csv"
            />
          </b-col>
        </b-row>
      </div>
    </fieldset>
  </div>
</template>

<script>
import PathMapper from "@/lib/PathMapper";
import { jsonClient } from "@/lib/raftApi";
import FileAttachmentListing from "@/components/FileAttachmentListing.vue";
import { useDrupalSettings } from "@/composables/useDrupalSettings";

const { config } = useDrupalSettings();

export default {
  name: "ChemicalContainerAttachments",
  components: {
    FileAttachmentListing,
  },
  props: {
    editMode: { type: Boolean, default: false },
    redirectAfterSave: { type: Boolean, default: true },
    uuid: { type: String, required: true },
  },
  data() {
    return {
      error: null,
      message: null,
      entity: null,
      deletedFiles: [],
      newFiles: [],
    };
  },
  computed: {
    getUploadPhotosIconPath: function () {
      return PathMapper.getIconAssetPath("images.png", this.$isB4);
    },
    showFileListing: function () {
      return this.entity?.field_chemical_container_files;
    },
    groupContextQueries() {
      const queries = {};
      if (config.groupContext) {
        queries["rog-type"] = "node";
        queries["rog-id"] = config.groupContext;
      }
      return queries;
    },
  },
  methods: {
    deleteAttachedFile: function (fileId) {
      this.deletedFiles.push(fileId);
    },
    async updateFileAttachments() {
      const newFileIds = await this.uploadNewFiles();
      await this.updateContainerPatch(newFileIds);
    },
    async uploadNewFiles() {
      const options = {
        entityType: "node",
        entityBundle: "chemical_container",
        fieldName: "field_chemical_container_files",
        queries: this.groupContextQueries,
        files: this.newFiles,
      };
      const responses = await jsonClient.postFilesToField(options);
      const newFileIds = [];
      responses.forEach((response) => {
        if (!response.data?.id) {
          this.errorEmit(response.toString(), response);
        } else {
          newFileIds.push(response.data.id);
        }
      });
      return newFileIds;
    },
    async updateContainerPatch(newFileIds) {
      const editData = {
        id: this.uuid,
        type: "node--chemical_container",
        attributes: {},
        relationships: {
          field_chemical_container_files: {
            data: [],
          },
        },
      };

      if (newFileIds.length || this.deletedFiles.length) {
        const notDeletedFileIds =
          this.entity.field_chemical_container_files.reduce(
            (filtered, fileObject) => {
              if (!this.deletedFiles.includes(fileObject.id)) {
                filtered.push(fileObject.id);
              }
              return filtered;
            },
            [],
          );
        editData.relationships.field_chemical_container_files.data = [
          ...notDeletedFileIds,
          ...newFileIds,
        ].map((uuid) => ({
          type: "file--file",
          id: uuid,
        }));
        const patchOptions = {
          path: "node/chemical_container/" + this.uuid,
          queries: this.groupContextQueries,
          data: editData,
        };
        await jsonClient
          .patch(patchOptions)
          .catch((error) => this.errorEmit(error.message, error.data));
      }
      // Redirect, depending on other components or for testing.
      if (this.redirectAfterSave) {
        this.redirectToDestination();
      }
    },
    async fetchContainerData() {
      const queries = {
        include: "field_chemical_container_files",
        "fields[file--file]":
          "changed,created,uri,filesize,filemime,filename,drupal_internal__fid",
        "fields[node--chemical_container]":
          "drupal_internal__nid,title,changed,created,uuid,field_chemical_container_files",
        ...this.groupContextQueries,
      };
      const response = await jsonClient
        .get({
          path: `/node/chemical_container/${this.uuid}`,
          queries: queries,
        })
        .catch((error) => this.errorEmit(error.message, error.data));
      this.entity = response?.deserialized;
    },
    errorEmit(message, data) {
      this.$showNotification("error", message);
    },
    redirectToDestination() {
      const urlParams = new URLSearchParams(window.location.search);
      const matchReg = /^\/*(.*)/;
      let destination = urlParams.get("destination");
      if (destination) {
        const [full, match] = matchReg.exec(destination);
        destination = "/" + match;
        if (PathMapper.isLocal(destination && full)) {
          window.location.assign(destination);
        }
      }
      destination = document.location.href.replace("/edit", "");
      // Handle redirecting whether we're in a group context or site context.
      window.location.assign(destination);
    },
  },
  mounted() {
    this.fetchContainerData();
    if (this.editMode) {
      // Event is fired from B3 JS, chemtracker.js updateChemicalContainer method.
      document.addEventListener("containerUpdate", this.updateFileAttachments);
    }
  },
  beforeDestroy() {
    if (this.editMode) {
      document.removeEventListener(
        "containerUpdate",
        this.updateFileAttachments,
      );
    }
  },
};
</script>

<style scoped>
.chem-container-attach-wrapper {
  margin-bottom: 1.5em;
}
</style>
